import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'
import useSWR from 'swr'
import { getActivityList, getActivityListUrl } from '@/apis/activity'
import {
  readHomeActivityListCache,
  writeHomeActivityListCache,
} from '@/constant/home-first-screen-cache'
import { userState } from '@/store/user'
import type { Activity, ActivityListResp } from '@/types/activity'
import {
  usePageInteractive,
  usePagePerformanceLifecycle,
} from '@/hooks/usePagePerformanceLifecycle'
import { shouldReserveHomeActivityPendingSlot } from '@/utils/homeActivityBannerSlot'

const persistActivityList = (userId: string, data: ActivityListResp) => {
  if (typeof window === 'undefined' || !Array.isArray(data.list)) return

  const write = () => writeHomeActivityListCache(userId, data.list)
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(write, { timeout: 2000 })
  } else {
    window.setTimeout(write, 0)
  }
}

export default function useHomeActivityList() {
  const user = useRecoilValue(userState)
  const pageLifecycle = usePagePerformanceLifecycle()
  const pageInteractive = usePageInteractive()
  const userId = user?.user_id ? String(user.user_id) : ''
  const fallbackData = useMemo<ActivityListResp | undefined>(() => {
    const snapshot = readHomeActivityListCache<Activity>(userId)
    return snapshot ? { list: snapshot.activities } : undefined
  }, [userId])
  const fetchActivityList = useCallback(async () => {
    const data = await getActivityList()
    persistActivityList(userId, data)
    return data
  }, [userId])

  const swr = useSWR([getActivityListUrl, userId], fetchActivityList, {
    fallbackData,
    revalidateOnMount: true,
  })
  const activityRequestPending = swr.isLoading || swr.isValidating
  const [hadPendingRequestBeforeInteractive, setHadPendingRequest] = useState(
    activityRequestPending
  )

  // 活动数据未就绪时先保留 banner cell；即使请求很快返回空，也等首页首屏终态
  // 上报后再释放，避免首屏关键期内 cell 插入/删除导致媒体目标重锁。
  useEffect(() => {
    if (!pageInteractive && activityRequestPending) {
      setHadPendingRequest(true)
    }
  }, [activityRequestPending, pageInteractive])

  return {
    ...swr,
    shouldReservePendingSlot: shouldReserveHomeActivityPendingSlot({
      hasPageLifecycle: Boolean(pageLifecycle),
      pageInteractive,
      activityRequestPending,
      hadPendingRequestBeforeInteractive,
    }),
  }
}
